import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import BackdropDialog from 'common/components/BackdropDialog';
import RolesSelect from './RolesSelect';
import PermissionsSelect, { PermissionUpdate } from './PermissionsSelect';
import LocationsSelect from './LocationsSelect';

import { ShopRole, UserApi } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import { getDefaultPermissionsForRoles } from 'common/modules/users/utils';
import { getShopRoles, isAdmin } from 'common/modules/users/utils';
import Banner from 'components/Banner';
import { getPermissionOptions } from '../utils';

interface Props {
	open: boolean;
	value: UserApi;
	onChange: (value: UserApi) => void;
	onClose: () => void;
	isOnlyAdmin?: boolean;
}

const EditUserDialog = (props: Props) => {
	const { open, value, isOnlyAdmin, onChange, onClose } = props;
	const classes = useStyles();
	const { t } = useTranslation();

	const activeShopId = useSelector(ShopSelectors.activeShopId)!;
	const userLocations = useSelector(UserSelectors.userLocations);
	const userHasAccessToAllLocations = useSelector(UserSelectors.userHasAccessToAllLocations);

	const [roles, setRoles] = useState<ShopRole[]>(
		getShopRoles(value.access?.[activeShopId]?.roles ?? []),
	);
	const [permissions, setPermissions] = useState(value.access?.[activeShopId]?.permissions ?? []);
	const [restrictions, setRestrictions] = useState(
		value.access?.[activeShopId]?.restrictions ?? [],
	);
	const [locations, setLocations] = useState(value.access?.[activeShopId]?.locations);

	const defaultPermissions = getDefaultPermissionsForRoles(roles);

	const handleRolesChange = (roles: ShopRole[]) => {
		setRoles(roles);
		if (isAdmin(roles)) {
			setPermissions([]);
			setRestrictions([]);
			setLocations(undefined);
		}
	};

	const handlePermissionChange = ({ permissions, restrictions }: PermissionUpdate): void => {
		setRestrictions(restrictions);
		setPermissions(permissions);
	};

	const handleConfirm = async () => {
		await onChange({
			...value,
			access: {
				...value.access,
				[activeShopId]: { roles, permissions, restrictions, locations },
			},
		});
	};

	return (
		<BackdropDialog
			isOpen={open}
			onClose={onClose}
			label={t('users.editUser', 'Edit user')}
			body={
				<Grid container spacing={3} alignItems="center">
					{isOnlyAdmin && (
						<Grid item xs={12}>
							<Banner
								variant="info"
								title={t(
									'users.editRolesDisabledTitle',
									'The role of the last admin user cannot be changed',
								)}
								description={t(
									'users.editRolesDisabledDescription',
									'If you want to remove the admin role from this user, please promote another user to admin first.',
								)}
							/>
						</Grid>
					)}
					<Grid item xs={12} sm={4}>
						<Typography variant="body1" className={classes.label}>
							{t('common:form.emailAddress')}
						</Typography>
					</Grid>
					<Grid item xs={12} sm={8}>
						<Typography variant="body1">{value.email}</Typography>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Typography variant="body1" className={classes.label}>
							{t('users.role')}
						</Typography>
					</Grid>
					<Grid item xs={12} sm={8}>
						<RolesSelect value={roles ?? []} onChange={handleRolesChange} disabled={isOnlyAdmin} />
					</Grid>
					<Grid item xs={12} sm={4}>
						<Typography variant="body1" className={classes.label}>
							{t('common:shopLocation.stores')}
						</Typography>
					</Grid>
					<Grid item xs={12} sm={8}>
						<LocationsSelect
							value={locations}
							onChange={setLocations}
							options={userLocations}
							disabled={!!roles.length && isAdmin(roles)}
							allowAllLocations={userHasAccessToAllLocations}
						/>
					</Grid>
					{isAdmin(roles) ? null : (
						<>
							<Grid item xs={12}>
								<Typography variant="body1" className={classes.label}>
									{t('users.editPermission', 'Edit permissions')}
								</Typography>
							</Grid>
							<Grid item xs={12}>
								<PermissionsSelect
									permissions={permissions}
									restrictions={restrictions}
									permissionOptions={getPermissionOptions()}
									onChange={handlePermissionChange}
									defaultPermissions={defaultPermissions}
								/>
							</Grid>
						</>
					)}
				</Grid>
			}
			variant="primary"
			zIndex={1300}
			confirm={{
				label: t('common:actions.save'),
				callback: handleConfirm,
				closeAfterDone: true,
				disabled: !roles.length || isOnlyAdmin,
			}}
			cancel={{
				label: t('common:actions.cancel'),
			}}
		/>
	);
};

const useStyles = makeStyles((theme) =>
	createStyles({
		label: {
			fontWeight: 500,
		},
	}),
);

export default EditUserDialog;
